import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Keeps the announcement bar's free-shipping remaining-amount message in sync
 * with the cart, the same way `<cart-icon>` keeps its count in sync: read the
 * cart totals straight off the `cartLinesUpdate` event and patch the DOM
 * directly. No network request, and no dependency on the cart drawer or on a
 * section re-render - this is fully self-contained.
 *
 * Note: unlike the server-rendered version, this always shows full decimal
 * precision (it doesn't replicate the `money_without_trailing_zeros` "$50" vs
 * "$50.00" behavior) - a minor cosmetic trade-off for staying free of a
 * network round-trip.
 *
 * @extends {Component}
 */
class FreeShippingInfo extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        if (cart) this.#updateFromCart(cart);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[free-shipping-info] Cart update promise rejected:', error);
      });
  };

  /**
   * @param {{ cost: { totalAmount: { amount: string, currencyCode: string } } }} cart
   */
  #updateFromCart(cart) {
    const messageEl = this.querySelector('[data-free-shipping-limit]');
    const amountEl = this.querySelector('[data-left-to-spend]');
    if (!messageEl || !amountEl) return;

    const currency = cart.cost.totalAmount.currencyCode;
    const limit = messageEl.getAttribute('data-free-shipping-limit') ?? '0';

    const totalCents = convertMoneyToMinorUnits(cart.cost.totalAmount.amount, currency) ?? 0;
    const limitCents = convertMoneyToMinorUnits(limit, currency) ?? 0;
    const qualifies = totalCents >= limitCents;

    messageEl.classList.toggle('is-success', qualifies);
    messageEl.dataset.freeShipping = String(qualifies);

    const format =
      this.dataset.currencyCodeEnable === 'true' ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;
    if (!format) return;

    amountEl.textContent = formatMoney(Math.max(limitCents - totalCents, 0), format, currency);
  }
}

if (!customElements.get('free-shipping-info')) {
  customElements.define('free-shipping-info', FreeShippingInfo);
}
